import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  BadgePercent,
  CheckCircle2,
  ClipboardCheck,
  ClipboardX,
  Clock,
  OctagonAlert,
  HandCoins,
  Hourglass,
  PackageCheck,
  PackageX,
  ShieldAlert,
  ShieldQuestion,
  ShieldX,
  ShoppingBag,
  Store,
  Ticket,
  TriangleAlert,
  Truck,
  Undo2,
  Warehouse,
  XCircle,
} from "lucide-react";
import type { ReturnWorkflowTriggerType } from "@babackapp/return-variables-registry";
import { useTrackingStatusMap } from "../nodes/trigger/use-tracking-status-list";
import { NodeColorKey } from "../lib/node-color-to-tailwind-classes";
import { Workflow } from "../types";

type TrackingStatusKey = keyof ReturnType<typeof useTrackingStatusMap>;

/**
 * Icon/accent per return trigger. The label itself reuses the
 * workflowsEditor.triggerTypes.<type>.title translation (same key as the
 * trigger node title and the create modal).
 */
const RETURN_TRIGGER_VISUALS: Record<
  ReturnWorkflowTriggerType,
  { Icon: LucideIcon; accent: NodeColorKey }
> = {
  return_created: { Icon: Undo2, accent: "green" },
  return_cancelled: { Icon: XCircle, accent: "default" },
  return_need_approval: { Icon: ShieldQuestion, accent: "orange" },
  return_disapproved: { Icon: ShieldX, accent: "default" },
  return_approved: { Icon: CheckCircle2, accent: "green" },
  return_merchant_approved: { Icon: PackageCheck, accent: "green" },
  return_merchant_rejected: { Icon: PackageX, accent: "default" },
  return_shipping_label_created: { Icon: Ticket, accent: "blue" },
  return_shipping_label_error: { Icon: TriangleAlert, accent: "default" },
  return_discount_created: { Icon: BadgePercent, accent: "yellow" },
  return_payment_refunded: { Icon: HandCoins, accent: "blue" },
  return_deposit_reminder: { Icon: Hourglass, accent: "orange" },
  return_resolution_updated: { Icon: ArrowLeftRight, accent: "blue" },
  return_logistician_approved: { Icon: ClipboardCheck, accent: "green" },
  return_logistician_rejected: { Icon: ClipboardX, accent: "default" },
  return_treatment_error: { Icon: OctagonAlert, accent: "default" },
  // Warehouse, not a truck: the trigger fires when the return *arrives*.
  return_delivered: { Icon: Warehouse, accent: "green" },
  // The truck belongs here: this one fires while the parcel is moving.
  return_in_transit: { Icon: Truck, accent: "blue" },
};

export interface TriggerLabel {
  typeLabel: string;
  summary?: string;
  Icon: LucideIcon;
  accent: NodeColorKey;
}

/**
 * Returns a short, human-readable label for a workflow trigger:
 * its type and a one-token config summary (e.g. the tracked status).
 */
export function useTriggerLabel(): (trigger: Workflow["trigger"]) => TriggerLabel {
  const { t } = useTranslation("trackoffice");
  const trackingStatusMap = useTrackingStatusMap();

  return (trigger) => {
    const { triggerType, config } = trigger.data;

    const returnVisuals =
      RETURN_TRIGGER_VISUALS[triggerType as ReturnWorkflowTriggerType];
    if (returnVisuals) {
      return {
        typeLabel: t(`workflowsEditor.triggerTypes.${triggerType}.title`),
        Icon: returnVisuals.Icon,
        accent: returnVisuals.accent,
      };
    }

    // Cast to string: claim_status_change is an extended type not yet in the
    // generated API client union.
    switch (triggerType as string) {
      case "tracking_status_change": {
        const status =
          "trackingStatus" in config ? config.trackingStatus : undefined;
        return {
          typeLabel: t(
            "workflowsEditor.triggers.trackingStatusChange",
            "Tracking status",
          ),
          summary: status
            ? trackingStatusMap[status as TrackingStatusKey]?.label
            : undefined,
          Icon: Truck,
          accent: "green",
        };
      }
      case "order_not_fulfilled": {
        const days =
          "minDaysSinceCreation" in config
            ? Number(config.minDaysSinceCreation)
            : undefined;
        return {
          typeLabel: t(
            "workflowsEditor.triggers.orderNotFulfilled",
            "Order not fulfilled",
          ),
          summary:
            days != null
              ? t("workflowsEditor.triggers.delayDays", "Delay of {count} day(s)", {
                  count: days,
                })
              : undefined,
          Icon: Clock,
          accent: "blue",
        };
      }
      case "order_created":
        return {
          typeLabel: t("workflowsEditor.triggers.orderCreated", "Order created"),
          Icon: ShoppingBag,
          accent: "yellow",
        };
      case "click_and_collect_status_change": {
        const status =
          "status" in config && typeof config.status === "string"
            ? config.status
            : undefined;
        return {
          typeLabel: t(
            "workflowsEditor.triggers.clickAndCollectStatusChange",
            "Click & collect",
          ),
          summary: status
            ? t(`clickAndCollect.status.${status}`, status.replace(/_/g, " "))
            : undefined,
          Icon: Store,
          accent: "orange",
        };
      }
      case "claim_status_change": {
        const claimConfig = config as {
          claimStatuses?: unknown;
          anyStatus?: boolean;
        };
        const statuses = Array.isArray(claimConfig.claimStatuses)
          ? (claimConfig.claimStatuses as string[])
          : [];
        return {
          typeLabel: t(
            "workflowsEditor.triggers.claimStatusChange",
            "Claim status",
          ),
          summary: claimConfig.anyStatus
            ? t("workflowsEditor.claimStatus.any", "Any status change")
            : statuses.length
            ? statuses
                .map((s) => t(`workflowsEditor.claimStatus.${s.toLowerCase()}`))
                .join(", ")
            : undefined,
          Icon: ShieldAlert,
          accent: "default",
        };
      }
      default:
        return {
          typeLabel: t("workflowsEditor.nodes.trigger.title", "Trigger"),
          Icon: Truck,
          accent: "green",
        };
    }
  };
}
